/**
 * The domain root (`/`).
 *
 * Three-way branch, and the ORDER of the branches is the fix for the marketing
 * flash a signed-in user used to see on `fias.io`. Background: the CloudFront
 * rewrite that serves prerendered site-arche snapshots is unconditional by
 * design (CLAUDE.md § Site Arches), so everybody — signed in or not — is served
 * the same cached marketing HTML at `/` and the app boots over the top of it.
 *
 * Lives in its own module rather than inline in `App.tsx` so the branching can
 * be unit-tested without pulling the entire route table's import graph in.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import { BootSplash } from '@/components/BootSplash';
import { hasBootSessionHint } from '@/lib/boot-session-hint';
import { hasSessionHint } from '@/lib/auth-refresh';
import { FiasSiteArchePage } from '@/arche/arches/fias-site';

export function Home() {
  const { isAuthenticated, isLoading, createGuestSession, isDevMode } = useAuth();
  const [returningVisitor] = React.useState(() => hasBootSessionHint() || hasSessionHint());
  const [guestLoading, setGuestLoading] = React.useState(false);
  const [guestError, setGuestError] = React.useState<string | null>(null);

  const handleTryFias = async () => {
    setGuestError(null);
    setGuestLoading(true);
    try {
      await createGuestSession();
      // On success the context sets `user`, isAuthenticated flips true, and the
      // redirect below fires on re-render — keep the spinner until then.
    } catch {
      setGuestLoading(false);
      setGuestError('Could not start a guest session. Please try again.');
    }
  };

  // Redirect authenticated users to the main arche interface. The splash rides
  // along because <Navigate> renders null and only moves the router in an
  // effect — without it this commit paints an empty #root between the snapshot
  // splash being torn down and the destination route mounting.
  if (isAuthenticated) {
    return (
      <>
        <BootSplash />
        <Navigate to="/a/arche_make_a_request" replace />
      </>
    );
  }

  // The loading branch is conditional on the visitor looking like a RETURNING
  // one, and that condition is the whole design.
  //
  // An unconditional skeleton would replace the prerendered snapshot with a
  // spinner and then swap back — a visible flash on the busiest page on the
  // site, imposed on every anonymous visitor and every crawler to spare the
  // minority who are signed in. So anonymous visitors keep falling straight
  // through to the marketing site below, exactly as before, and only a browser
  // that has actually held a session waits on the indicator instead of being
  // shown marketing it is about to be redirected away from.
  //
  // Two signals, because they cover different deployments. `hasBootSessionHint`
  // reads the attribute the snapshot's inline script set before first paint —
  // the production path, where the marketing HTML is already on screen and this
  // render has to agree with the splash covering it. `hasSessionHint` reads the
  // same localStorage key directly, which is what dev and any non-snapshot
  // route have (there is no inline script there to set the attribute).
  //
  // Snapshotted at mount rather than read per render: `getCurrentUser` clears
  // the localStorage key when it settles on logged-out, and re-reading it live
  // would flip this branch mid-boot for reasons unrelated to what is on screen.
  if (isLoading && returningVisitor) {
    return <BootSplash />;
  }

  // Signed-out visitors get the public marketing site at the domain root.
  // Crawlers are always signed out, so this is the branch they see — and the
  // one the build-time snapshot prerenders. The site is reachable at its
  // canonical /a/fias-site URL too, including for signed-in users.
  return (
    <>
      <FiasSiteArchePage basePath="/" />

      {/* Guest Trial Mode entry. Dev-gated for now; Phase 5 wires the real
          flag (guest_mode_enabled) + organic / paid-arrival issuance. Kept
          outside the marketing components deliberately: that surface must stay
          free of auth state so it can be rendered to static HTML at build
          time. */}
      {isDevMode && (
        <div className="fixed bottom-4 left-4 z-50">
          <button
            onClick={handleTryFias}
            disabled={guestLoading}
            className="rounded-md border bg-card px-4 h-10 text-sm font-medium text-muted-foreground shadow-sm transition-colors hover:text-foreground disabled:opacity-60"
          >
            {guestLoading ? 'Starting…' : 'Dev: try Fias without an account'}
          </button>
          {guestError && <p className="mt-2 max-w-xs text-sm text-destructive">{guestError}</p>}
        </div>
      )}
    </>
  );
}
